import React, { useMemo, useState } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    ReferenceLine, ReferenceArea, ReferenceDot, ResponsiveContainer, Area, AreaChart,
} from 'recharts';
import { fetchHistoryRaw } from '../../services/dataApi';
import { formatInTZ } from '../../utils/timezone';
import {
    PROPERTY_LABELS, CHART_COLORS,
    sectionStyle, inputStyle, labelStyle, btnStyle, toLocalDT, DataRow,
} from './surveillanceShared';
import { Reading } from '../../utils/recoveryModel';
import { timeAboveLimit } from '../../utils/exposureModel';
import { ThresholdSettings } from '../../services/advancedRulesApi';
import NumField from './NumField';
import { THRESHOLD_PRESETS } from '../../utils/thresholdPresets';
import {
    runExcursions, evaluateOutcome, AlarmEvent, RuleKind,
} from '../../utils/alarmRules';

/**
 * Tröskel-labbet — styrd komponent. Inställningarna ägs av fliken Avancerat, så
 * det du provar fram här är exakt det som sparas. Labbet äger bara sin egen
 * mätdata, tidsperiod och markerade fel.
 */
interface ThresholdLabProps {
    ieee: string;
    property: string;
    deviceName: string;
    settings: ThresholdSettings;
    onSettingsChange: (s: ThresholdSettings) => void;
    timezone?: string | null;
    isReadOnly?: boolean;
}

const TEMP_COLOR = CHART_COLORS[0];      // blå — temperatur
const EXC_COLOR = '#6a1b9a';             // lila — kommer inte tillbaka
const BUDGET_COLOR = '#d84315';          // djuporange — sammanlagd tid i värme
const FAULT_COLOR = '#c62828';           // röd — markerat känt fel
const WINDOW_H = 24;                     // rullande fönster för tid-i-värme (timmar)

const RULE_COLOR: Record<RuleKind, string> = {
    excursion: EXC_COLOR, budget: BUDGET_COLOR,
};
const RULE_LABEL: Record<RuleKind, string> = {
    excursion: 'Kom inte tillbaka', budget: 'Tid i värme',
};

const formatISO = (d: Date): string => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const fmtDur = (ms: number): string => {
    const h = ms / 3600e3;
    return h < 1 ? `${Math.round(ms / 60000)} min` : `${h.toFixed(1)} h`;
};

const ThresholdLab: React.FC<ThresholdLabProps> = ({
    ieee, property, deviceName, settings, onSettingsChange, timezone, isReadOnly,
}) => {
    const [fromStr, setFromStr] = useState<string>(() => toLocalDT(new Date(Date.now() - 14 * 24 * 3600e3)));
    const [toStr, setToStr] = useState<string>(() => toLocalDT(new Date()));

    // ── Markerade kända fel ───────────────────────────────────────────────────
    const [faults, setFaults] = useState<{ start: number; end: number }[]>([]);
    const [dragStart, setDragStart] = useState<number | null>(null);
    const [dragEnd, setDragEnd] = useState<number | null>(null);

    // ── Data ──────────────────────────────────────────────────────────────────
    const [rows, setRows] = useState<Reading[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loadedLabel, setLoadedLabel] = useState<string>('');

    const set =<K extends keyof ThresholdSettings>(key: K, value: ThresholdSettings[K]) =>
        onSettingsChange({ ...settings, [key]: value });

    const loadData = async () => {
        if (!ieee || !property) return;
        setLoading(true);
        setError(null);
        try {
            const rows = await fetchHistoryRaw<DataRow>({ ieees: ieee, from: formatISO(new Date(fromStr)), to: formatISO(new Date(toStr)), properties: property }, 60000);
            const reads: Reading[] = rows
                .filter(r => r.property === property && r.value_num !== null)
                .map(r => ({ ts: new Date(r.timestamp).getTime(), value: Number(r.value_num) }))
                .filter(r => !isNaN(r.ts))
                .sort((a, b) => a.ts - b.ts);
            setRows(reads);
            setFaults([]);
            const typicalGap = reads.length > 1
                ? [...Array(reads.length - 1).keys()].map(i => reads[i + 1].ts - reads[i].ts).sort((a, b) => a - b)[Math.floor((reads.length - 1) / 2)]
                : 0;
            setLoadedLabel(
                `${deviceName} · ${PROPERTY_LABELS[property] ?? property} · ${reads.length} mätvärden · ` +
                `oftast ${typicalGap ? fmtDur(typicalGap) : '—'} mellan mätningarna`
            );
        } catch (e: any) {
            const msg = e?.code === 'ECONNABORTED'
                ? 'Förfrågan tog för lång tid'
                : (e?.response?.data?.message ?? e?.message ?? 'Kunde inte hämta data');
            setError(msg);
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    // ── Kör reglerna ──────────────────────────────────────────────────────────
    const { chartData, events, excs, exposure, outcome } = useMemo(() => {
        const excs = settings.excursionOn
            ? runExcursions(rows, {
                armLevel: settings.armLevel,
                recoverLevel: settings.recoverLevel,
                ceilingLevel: settings.ceilingLevel,
                deadlineMinutes: settings.deadlineMin,
                extendWhileFalling: settings.extendWhileFalling,
            })
            : [];
        // timeAboveLimit räknar i timmar; inställningen anges i minuter.
        const exposure = timeAboveLimit(rows, {
            limit: settings.safeLimit, budget: settings.budgetMinutes / 60, windowH: WINDOW_H,
        });

        const valueAt = new Map(rows.map(r => [r.ts, r.value]));
        const events: AlarmEvent[] = [];
        for (const x of excs) {
            if (x.firedAt !== null) {
                events.push({
                    ts: x.firedAt, rule: 'excursion', value: valueAt.get(x.firedAt) ?? null,
                    reason: x.outcome === 'ceiling'
                        ? `gick över ${settings.ceilingLevel}°`
                        : `kom inte tillbaka under ${settings.recoverLevel}° i tid`,
                });
            }
        }
        if (settings.budgetOn) {
            for (const ts of exposure.crossTs) {
                events.push({
                    ts, rule: 'budget', value: valueAt.get(ts) ?? null,
                    reason: `mer än ${fmtDur(settings.budgetMinutes * 60000)} varmare än ${settings.safeLimit}° på ett dygn`,
                });
            }
        }
        events.sort((a, b) => a.ts - b.ts);

        const expByTs = new Map(exposure.series.map(p => [p.ts, p.hours]));
        const chartData = rows.map(r => ({ ts: r.ts, temp: r.value, exposure: expByTs.get(r.ts) ?? 0 }));

        const spanMs = rows.length > 1 ? rows[rows.length - 1].ts - rows[0].ts : 0;
        const outcome = evaluateOutcome(events, faults, spanMs);

        return { chartData, events, excs, exposure, outcome };
    }, [rows, settings, faults]);

    const tempAt = useMemo(() => new Map(chartData.map(d => [d.ts, d.temp])), [chartData]);

    const ruleCard = (
        title: string, color: string, on: boolean, setOn: (b: boolean) => void,
        count: number, children: React.ReactNode, hint: string,
    ) => (
        <div style={{ border: `1px solid ${on ? color : '#ddd'}`, borderLeft: `4px solid ${on ? color : '#ddd'}`, borderRadius: 6, padding: '12px 14px', marginBottom: 12, opacity: on ? 1 : 0.55 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <input type="checkbox" checked={on} disabled={isReadOnly} onChange={e => setOn(e.target.checked)} />
                <strong style={{ color }}>{title}</strong>
                <span style={{ fontSize: 13, color: '#666' }}>
                    {rows.length === 0 ? 'ingen mätdata hämtad' : `hade larmat ${count} ${count === 1 ? 'gång' : 'gånger'}`}
                </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>{children}</div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>{hint}</div>
        </div>
    );

    const excCount = events.filter(e => e.rule === 'excursion').length;
    const budgetCount = events.filter(e => e.rule === 'budget').length;

    const cameBackOnOwn = excs.filter(x => x.outcome === 'recovered').length;
    const longestGapInFiring = excs
        .filter(x => x.firedAt !== null)
        .reduce((m, x) => Math.max(m, x.maxGapMs), 0);

    return (
        <div>
            {/* ── Hämta mätdata ── */}
            <div style={sectionStyle}>
                <h4 style={{ marginTop: 0, fontSize: 14 }}>1. Hämta gammal mätdata</h4>
                <p style={{ color: '#666', fontSize: 13, marginTop: -4 }}>
                    Kör historiken för <strong>{deviceName}</strong> mot inställningarna nedan och se vad som
                    hade hänt. Allt räknas här i webbläsaren — inga riktiga larm påverkas.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
                    <div>
                        <label style={labelStyle}>Från</label>
                        <input type="datetime-local" style={inputStyle} value={fromStr} onChange={e => setFromStr(e.target.value)} />
                    </div>
                    <div>
                        <label style={labelStyle}>Till</label>
                        <input type="datetime-local" style={inputStyle} value={toStr} onChange={e => setToStr(e.target.value)} />
                    </div>
                    <button style={btnStyle('#0f3460')} onClick={loadData} disabled={loading || !ieee || !property}>
                        {loading ? 'Hämtar…' : 'Hämta mätdata'}
                    </button>
                </div>
                {loadedLabel && !error && <div style={{ marginTop: 10, fontSize: 12, color: '#0f3460' }}>{loadedLabel}</div>}
                {error && <div style={{ marginTop: 10, fontSize: 13, color: '#c0392b' }}>{error}</div>}
            </div>

            {/* ── Inställningar ── */}
            <div style={sectionStyle}>
                <h4 style={{ marginTop: 0, fontSize: 14 }}>2. Prova inställningarna</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                    <span style={{ fontSize: 13, color: '#666' }}>Börja med:</span>
                    {THRESHOLD_PRESETS.map(p => (
                        <button
                            key={p.name} style={btnStyle('#455a64')} disabled={isReadOnly}
                            onClick={() => onSettingsChange({ ...p.settings })}
                        >
                            {p.name}
                        </button>
                    ))}
                </div>

                {ruleCard('Kommer inte tillbaka', EXC_COLOR, settings.excursionOn, b => set('excursionOn', b), excCount, (
                    <>
                        <NumField label="Blir varmare än" value={settings.armLevel} onChange={v => set('armLevel', v)} unit="°C" disabled={isReadOnly} />
                        <NumField label="Måste tillbaka under" value={settings.recoverLevel} onChange={v => set('recoverLevel', v)} unit="°C" disabled={isReadOnly} />
                        <NumField label="Inom" value={settings.deadlineMin} onChange={v => set('deadlineMin', v)} unit="min" disabled={isReadOnly} />
                        <NumField label="Larmar direkt över" value={settings.ceilingLevel} onChange={v => set('ceilingLevel', v)} unit="°C" disabled={isReadOnly} />
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, alignSelf: 'flex-end', paddingBottom: 8 }}>
                            <input
                                type="checkbox" checked={settings.extendWhileFalling} disabled={isReadOnly}
                                onChange={e => set('extendWhileFalling', e.target.checked)}
                            />
                            Räkna bara tid när det inte blir bättre
                        </label>
                    </>
                ), 'Enheten får bli varm — den måste bara bli kall igen i tid. Blir den varmare än första gränsen startar en klocka, och den måste vara nere under andra gränsen innan tiden går ut. Kryssar du i rutan pausas klockan medan temperaturen sjunker och går igen så fort den står still eller stiger. Då hinner en långsam men äkta återhämtning bli klar, samtidigt som en trött kompressor som pendlar upp och ner utan att nå hem samlar på sig tid vid varje uppgång tills det larmar. Den här regeln behöver byggas i larmmotorn först.')}

                {ruleCard('Sammanlagd tid i värme', BUDGET_COLOR, settings.budgetOn, b => set('budgetOn', b), budgetCount, (
                    <>
                        <NumField label="Varmare än" value={settings.safeLimit} onChange={v => set('safeLimit', v)} unit="°C" disabled={isReadOnly} />
                        <NumField label="Tillåtet per dygn" value={settings.budgetMinutes} onChange={v => set('budgetMinutes', v)} unit="min" disabled={isReadOnly} />
                    </>
                ), 'Räknar ihop hur länge det varit för varmt det senaste dygnet. Gammal värme räknas bort när den blir mer än ett dygn gammal. Handlar om maten, inte om utrustningen.')}

                {settings.excursionOn && settings.recoverLevel >= settings.armLevel && (
                    <div style={{ fontSize: 13, color: '#c0392b', marginTop: 4 }}>
                        ⚠ "Måste tillbaka under" bör vara lägre än "blir varmare än". Annars startar och slutar larmet om och om igen.
                    </div>
                )}
                {longestGapInFiring > 2 * 3600e3 && (
                    <div style={{ fontSize: 13, color: '#e65100', marginTop: 4 }}>
                        ⚠ Ett av larmen bygger på ett hål i mätningarna på {fmtDur(longestGapInFiring)}. Regeln utgår från att det var fortsatt varmt under tiden sensorn var tyst.
                    </div>
                )}
            </div>

            {/* ── Graf ── */}
            <div style={sectionStyle}>
                {chartData.length === 0 ? (
                    <p style={{ color: '#888', textAlign: 'center', padding: '48px 0', margin: 0 }}>
                        Ingen mätdata hämtad — välj tidsperiod ovan och klicka "Hämta mätdata".
                    </p>
                ) : (
                    <>
                        <ResponsiveContainer width="100%" height={320}>
                            <LineChart
                                data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }} syncId="lab"
                                onMouseDown={(e: any) => { if (e && e.activeLabel != null) { setDragStart(Number(e.activeLabel)); setDragEnd(Number(e.activeLabel)); } }}
                                onMouseMove={(e: any) => { if (dragStart != null && e && e.activeLabel != null) setDragEnd(Number(e.activeLabel)); }}
                                onMouseUp={() => {
                                    if (dragStart != null && dragEnd != null) {
                                        const a = Math.min(dragStart, dragEnd), b = Math.max(dragStart, dragEnd);
                                        if (b > a) setFaults(w => [...w, { start: a, end: b }]);
                                    }
                                    setDragStart(null); setDragEnd(null);
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="ts" type="number" scale="time" domain={['dataMin', 'dataMax']}
                                    tickFormatter={ts => formatInTZ(ts as number, timezone, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    tick={{ fontSize: 11 }} minTickGap={60}
                                />
                                <YAxis width={56} tick={{ fontSize: 11 }} label={{ value: '°C', angle: -90, position: 'insideLeft', fontSize: 11 }} />

                                {faults.map((w, i) => (
                                    <ReferenceArea key={'f' + i} x1={w.start} x2={w.end} fill={FAULT_COLOR} fillOpacity={0.14} ifOverflow="extendDomain" />
                                ))}
                                {dragStart != null && dragEnd != null && dragStart !== dragEnd && (
                                    <ReferenceArea x1={Math.min(dragStart, dragEnd)} x2={Math.max(dragStart, dragEnd)} fill={FAULT_COLOR} fillOpacity={0.28} ifOverflow="extendDomain" />
                                )}

                                {settings.excursionOn && <ReferenceLine y={settings.ceilingLevel} stroke={EXC_COLOR} strokeDasharray="2 6" label={{ value: `larmar direkt ${settings.ceilingLevel}°`, fontSize: 11, fill: EXC_COLOR, position: 'insideTopRight' }} />}
                                {settings.excursionOn && <ReferenceLine y={settings.armLevel} stroke={EXC_COLOR} strokeDasharray="4 4" label={{ value: `klockan startar ${settings.armLevel}°`, fontSize: 11, fill: EXC_COLOR, position: 'insideBottomRight' }} />}
                                {settings.excursionOn && <ReferenceLine y={settings.recoverLevel} stroke={EXC_COLOR} strokeDasharray="1 4" label={{ value: `tillbaka under ${settings.recoverLevel}°`, fontSize: 11, fill: EXC_COLOR, position: 'insideBottomLeft' }} />}

                                <Tooltip
                                    labelFormatter={ts => formatInTZ(ts as number, timezone)}
                                    formatter={(value: any) => [`${Number(value).toFixed(1)} °C`, 'Temperatur']}
                                    contentStyle={{ fontSize: 12 }}
                                />
                                <Line type="monotone" dataKey="temp" stroke={TEMP_COLOR} strokeWidth={2} dot={false} connectNulls />

                                {events.map((e, i) => (
                                    <ReferenceDot
                                        key={'e' + i} x={e.ts} y={tempAt.get(e.ts) ?? 0} r={5}
                                        fill={RULE_COLOR[e.rule]} stroke="#fff" strokeWidth={1.5} ifOverflow="extendDomain"
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>

                        <div style={{ marginTop: 10, fontSize: 13, color: '#444' }}>
                            🖱️ <strong>Dra över grafen</strong> för att märka ut en period där du <span style={{ color: FAULT_COLOR, fontWeight: 600 }}>vet att något var fel</span>.
                            Allt utanför markeringarna räknas som normal drift, så larm där räknas som larm i onödan.
                        </div>
                        {faults.length > 0 && (
                            <div style={{ marginTop: 8 }}>
                                {faults.map((w, i) => (
                                    <div key={i} style={{ fontSize: 12, marginBottom: 2 }}>
                                        <span style={{ color: FAULT_COLOR }}>#{i + 1}</span>{' '}
                                        {formatInTZ(w.start, timezone)} – {formatInTZ(w.end, timezone)} ({fmtDur(w.end - w.start)}){' '}
                                        <button style={{ ...btnStyle('#c0392b'), padding: '1px 8px', fontSize: 11, marginLeft: 6 }}
                                            onClick={() => setFaults(ws => ws.filter((_, j) => j !== i))}>
                                            ta bort
                                        </button>
                                    </div>
                                ))}
                                <button style={{ ...btnStyle('#888'), marginTop: 6 }} onClick={() => setFaults([])}>Rensa alla</button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ── Resultat ── */}
            {chartData.length > 0 && (
                <div style={sectionStyle}>
                    <h4 style={{ marginTop: 0, fontSize: 14 }}>3. Resultat</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28, marginBottom: 12 }}>
                        <div>
                            <div style={{ fontSize: 24, fontWeight: 700, color: outcome.missed > 0 ? '#c0392b' : '#2e7d32' }}>
                                {outcome.caught}/{faults.length}
                            </div>
                            <div style={{ fontSize: 12, color: '#666' }}>fel som hade upptäckts</div>
                        </div>
                        <div>
                            <div style={{ fontSize: 24, fontWeight: 700, color: outcome.falseAlarms > 0 ? '#e65100' : '#2e7d32' }}>
                                {outcome.falseAlarms}
                            </div>
                            <div style={{ fontSize: 12, color: '#666' }}>
                                larm i onödan · {outcome.falseAlarmsPerWeek.toFixed(1)} per vecka räknat på {outcome.spanDays.toFixed(1)} dygn
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: 24, fontWeight: 700, color: '#0f3460' }}>{cameBackOnOwn}</div>
                            <div style={{ fontSize: 12, color: '#666' }}>gånger den blev varm men kom tillbaka själv</div>
                        </div>
                        <div>
                            <div style={{ fontSize: 24, fontWeight: 700, color: '#0f3460' }}>{fmtDur(exposure.peakHours * 3600e3)}</div>
                            <div style={{ fontSize: 12, color: '#666' }}>mest tid varmare än {settings.safeLimit}° på ett dygn</div>
                        </div>
                    </div>

                    {faults.length === 0 ? (
                        <div style={{ fontSize: 13, color: '#888' }}>
                            Märk ut minst en period med känt fel i grafen för att se hur snabbt larmet hade kommit.
                            Utan markeringar visas bara hur ofta det hade larmat — vilket är det du vill veta för en
                            period du vet var problemfri.
                        </div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr style={{ textAlign: 'left', color: '#666', borderBottom: '1px solid #eee' }}>
                                    <th style={{ padding: '6px 8px' }}>Fel</th>
                                    <th style={{ padding: '6px 8px' }}>Började</th>
                                    <th style={{ padding: '6px 8px' }}>Larmet kom</th>
                                    <th style={{ padding: '6px 8px' }}>Tid till larm</th>
                                    <th style={{ padding: '6px 8px' }}>Vilken regel</th>
                                </tr>
                            </thead>
                            <tbody>
                                {outcome.rows.map((r, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                        <td style={{ padding: '6px 8px' }}>#{i + 1}</td>
                                        <td style={{ padding: '6px 8px' }}>{formatInTZ(r.incident.start, timezone)}</td>
                                        <td style={{ padding: '6px 8px' }}>
                                            {r.firstEventTs ? formatInTZ(r.firstEventTs, timezone)
                                                : <span style={{ color: '#c0392b', fontWeight: 600 }}>larmade aldrig</span>}
                                        </td>
                                        <td style={{ padding: '6px 8px' }}>
                                            {r.latencyMinutes != null ? fmtDur(r.latencyMinutes * 60000) : '—'}
                                        </td>
                                        <td style={{ padding: '6px 8px', color: r.rule ? RULE_COLOR[r.rule] : '#888', fontWeight: 600 }}>
                                            {r.rule ? RULE_LABEL[r.rule] : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* ── Kurva för tid i värme ── */}
            {chartData.length > 0 && settings.budgetOn && (
                <div style={sectionStyle}>
                    <h4 style={{ marginTop: 0, fontSize: 14, marginBottom: 8 }}>
                        Tid i värme det senaste dygnet
                        <span style={{ color: '#888', fontWeight: 400 }}> · larmar när kurvan går över det tillåtna</span>
                    </h4>
                    <ResponsiveContainer width="100%" height={200}>
                        <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }} syncId="lab">
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis
                                dataKey="ts" type="number" scale="time" domain={['dataMin', 'dataMax']}
                                tickFormatter={ts => formatInTZ(ts as number, timezone, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                tick={{ fontSize: 11 }} minTickGap={60}
                            />
                            <YAxis width={52} tick={{ fontSize: 11 }} label={{ value: 'h', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                            {faults.map((w, i) => (
                                <ReferenceArea key={'bf' + i} x1={w.start} x2={w.end} fill={FAULT_COLOR} fillOpacity={0.14} ifOverflow="extendDomain" />
                            ))}
                            <ReferenceLine y={settings.budgetMinutes / 60} stroke={BUDGET_COLOR} strokeDasharray="4 4" label={{ value: `tillåtet ${fmtDur(settings.budgetMinutes * 60000)}`, fontSize: 11, fill: BUDGET_COLOR, position: 'insideTopRight' }} />
                            <Tooltip
                                labelFormatter={ts => formatInTZ(ts as number, timezone)}
                                formatter={(value: any) => [`${Number(value).toFixed(1)} h för varmt`, 'Tid i värme']}
                                contentStyle={{ fontSize: 12 }}
                            />
                            <Area type="monotone" dataKey="exposure" stroke={BUDGET_COLOR} fill={BUDGET_COLOR} fillOpacity={0.2} strokeWidth={1.5} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* ── Larmen ── */}
            {events.length > 0 && (
                <div style={sectionStyle}>
                    <h4 style={{ marginTop: 0 }}>Larm som hade gått ({events.length})</h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ textAlign: 'left', color: '#666', borderBottom: '1px solid #eee' }}>
                                <th style={{ padding: '6px 8px' }}>#</th>
                                <th style={{ padding: '6px 8px' }}>Tid</th>
                                <th style={{ padding: '6px 8px' }}>Regel</th>
                                <th style={{ padding: '6px 8px' }}>Temperatur</th>
                                <th style={{ padding: '6px 8px' }}>Varför</th>
                                <th style={{ padding: '6px 8px' }}>Verkligt fel?</th>
                            </tr>
                        </thead>
                        <tbody>
                            {events.map((e, i) => {
                                const inside = faults.some(w => e.ts >= w.start && e.ts <= w.end);
                                return (
                                    <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                        <td style={{ padding: '6px 8px' }}>{i + 1}</td>
                                        <td style={{ padding: '6px 8px' }}>{formatInTZ(e.ts, timezone)}</td>
                                        <td style={{ padding: '6px 8px', color: RULE_COLOR[e.rule], fontWeight: 600 }}>{RULE_LABEL[e.rule]}</td>
                                        <td style={{ padding: '6px 8px' }}>{e.value != null ? `${e.value.toFixed(1)} °C` : '—'}</td>
                                        <td style={{ padding: '6px 8px', color: '#666' }}>{e.reason}</td>
                                        <td style={{ padding: '6px 8px' }}>
                                            {faults.length === 0 ? '—'
                                                : inside ? <span style={{ color: '#2e7d32', fontWeight: 600 }}>ja</span>
                                                    : <span style={{ color: '#e65100', fontWeight: 600 }}>i onödan</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

        </div>
    );
};

export default ThresholdLab;
